import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import { PlusIcon, MagnifyingGlassIcon, ChartBarIcon } from '@heroicons/react/24/outline'
import { Box } from '@mui/material'
import { PageWrapper } from '../../../shared/components'
import { Button, Input, Spinner } from '../../../interaxDesignSystemComponents'
import { useReports, useDeleteReport } from '../../../apiLayer'
import { ReportStatus } from 'pure-js/enums/ReportStatus'
import { INTERAX_COLORS } from '../../../constants/colors'
import { PageHeader } from '../../../shared/components/PageHeader'
import type { Report as PureJsReport } from 'pure-js/types/Report'
import { FacilitatorRoutes } from '../../../config/routes/routeEnums'
import { DeleteReportModal } from '../components/Reports/DeleteReportModal'

interface ReportSection {
  id: string
  title: string
  count: number
  isExpanded: boolean
  reports: PureJsReport[]
}

export const ReportsManagementPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    drafts: true,
    finalized: true
  })
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [reportToDelete, setReportToDelete] = useState<PureJsReport | null>(null)
  const deleteReportMutation = useDeleteReport()

  // Debounce search term
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedSetSearchTerm = useCallback((value: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value)
    }, 300)
  }, [])

  // Fetch reports using the API layer with debounced search term
  const {
    data: reportsResponse,
    isLoading,
    error,
    refetch: refetchReports
  } = useReports({ search: debouncedSearchTerm }, { enabled: true })

  // Track initial load completion
  React.useEffect(() => {
    if (!isLoading) {
      setIsInitialLoad(false)
    }
  }, [isLoading])

  // Group reports by status
  const reportSections = useMemo((): ReportSection[] => {
    const reports = (reportsResponse?.data || []) as PureJsReport[]

    // Filter by search term if provided
    const filteredReports = debouncedSearchTerm
      ? reports.filter((report) => report.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
      : reports

    const drafts = filteredReports.filter((report) => report.status === ReportStatus.DRAFT)
    const finalized = filteredReports.filter((report) => report.status === ReportStatus.FINALIZED)

    // Sort reports by date (newest first)
    // Use updatedAt for drafts (most recently edited) and finalizedAt or updatedAt for finalized
    const sortByDate = (a: PureJsReport, b: PureJsReport) => {
      const dateA = a.updatedAt || a.createdAt || ''
      const dateB = b.updatedAt || b.createdAt || ''
      // Sort newest first (descending)
      return new Date(dateB).getTime() - new Date(dateA).getTime()
    }

    const sortedDrafts = [...drafts].sort(sortByDate)
    const sortedFinalized = [...finalized].sort(sortByDate)

    return [
      {
        id: 'drafts',
        title: t('facilitator.reportManagement.drafts'),
        count: sortedDrafts.length,
        isExpanded: expandedSections.drafts,
        reports: sortedDrafts
      },
      {
        id: 'finalized',
        title: t('facilitator.reportManagement.finalized'),
        count: sortedFinalized.length,
        isExpanded: expandedSections.finalized,
        reports: sortedFinalized
      }
    ]
  }, [reportsResponse, expandedSections, debouncedSearchTerm, t])

  const handleGenerateReport = () => {
    navigate(FacilitatorRoutes.GENERATE_REPORT)
  }

  const handleContinueReport = (report: PureJsReport) => {
    navigate(FacilitatorRoutes.GENERATE_REPORT, {
      state: {
        reportId: report.id
      }
    })
  }

  const handleOpenReport = (report: PureJsReport) => {
    navigate(`${FacilitatorRoutes.GENERATE_REPORT}/report/${report.id}`, {
      state: {
        report: report // Pass the full report data via navigation state
      }
    })
  }

  const handleDeleteClick = useCallback((report: PureJsReport) => {
    setReportToDelete(report)
    setDeleteModalOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!reportToDelete) {
      console.warn('[ReportsManagementPage] Delete confirmed but no report to delete')
      return
    }
    try {
      await deleteReportMutation.mutateAsync(reportToDelete.id)
      setDeleteModalOpen(false)
      setReportToDelete(null)
      // Explicitly refetch reports list to ensure UI updates
      await refetchReports()
    } catch (error) {
      console.error('[ReportsManagementPage] Failed to delete report:', error)
      throw error // Re-throw so DeleteReportModal can handle it
    }
  }, [reportToDelete, deleteReportMutation, refetchReports])

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false)
    setReportToDelete(null)
  }, [])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSetSearchTerm(value)
  }

  const handleToggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  // Only show full-page spinner on initial load
  if (isLoading && isInitialLoad) {
    return (
      <PageWrapper>
        <Spinner size="large" color="sun" text={t('facilitator.reportManagement.loadingReports')} centered />
      </PageWrapper>
    )
  }

  if (error) {
    return (
      <PageWrapper sx={{ textAlign: 'center' }}>
        <Box className="text-body-1" sx={{ color: INTERAX_COLORS.sun }}>
          {t('facilitator.reportManagement.errorLoading', { message: error.message })}
        </Box>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header with Title, Description, and Button */}
      <PageHeader
        badgeText="REPORTS"
        badgeIcon={<ChartBarIcon />}
        title={t('facilitator.reportManagement.title')}
        subtitle={t('facilitator.reportManagement.description')}
        actions={
          <Button
            variant="primary"
            onClick={handleGenerateReport}
            icon={<PlusIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            {t('facilitator.reportManagement.generateReport')}
          </Button>
        }
      />

      {/* Search Input */}
      <Box
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Input
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('facilitator.reportManagement.searchPlaceholder')}
            startIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            fullWidth
            size="medium"
          />
        </Box>
      </Box>

      {/* Inline loading indicator for search filtering */}
      {isLoading && !isInitialLoad && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Spinner size="small" color="sun" />
            <Box sx={{ color: INTERAX_COLORS.white, fontSize: '14px' }}>Searching...</Box>
          </Box>
        </Box>
      )}

      {/* Report Sections */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          opacity: isLoading && !isInitialLoad ? 0.5 : 1,
          transition: 'opacity 0.2s ease'
        }}
      >
        {reportSections.map((section) => (
          <ReportSectionAccordion
            key={section.id}
            section={section}
            onToggle={handleToggleSection}
            onContinue={handleContinueReport}
            onOpen={handleOpenReport}
            onDelete={handleDeleteClick}
          />
        ))}
      </Box>

      {/* Delete Report Modal */}
      <DeleteReportModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        report={reportToDelete}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteReportMutation.isPending}
      />
    </PageWrapper>
  )
}

interface ReportSectionAccordionProps {
  section: ReportSection
  onToggle: (sectionId: string) => void
  onContinue: (report: PureJsReport) => void
  onOpen: (report: PureJsReport) => void
  onDelete: (report: PureJsReport) => void
}

const ReportSectionAccordion: React.FC<ReportSectionAccordionProps> = ({
  section,
  onToggle,
  onContinue,
  onOpen,
  onDelete
}) => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '8px',
        padding: '16px'
      }}
    >
      <Box
        onClick={() => onToggle(section.id)}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          marginBottom: section.isExpanded ? '16px' : 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box className="text-headline-3" sx={{ color: INTERAX_COLORS.white }}>
            {section.title}
          </Box>
          <Box
            sx={{
              backgroundColor: INTERAX_COLORS.thunderGrey,
              borderRadius: '12px',
              padding: '4px 8px',
              fontSize: '12px',
              color: INTERAX_COLORS.white
            }}
          >
            {section.count}
          </Box>
        </Box>
        <Box sx={{ color: INTERAX_COLORS.white }}>{section.isExpanded ? '−' : '+'}</Box>
      </Box>

      {section.isExpanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {section.reports.length === 0 ? (
            <Box className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, padding: '16px' }}>
              {t('facilitator.reportManagement.noReports')}
            </Box>
          ) : (
            section.reports.map((report) => (
              <ReportCard key={report.id} report={report} onContinue={onContinue} onOpen={onOpen} onDelete={onDelete} />
            ))
          )}
        </Box>
      )}
    </Box>
  )
}

interface ReportCardProps {
  report: PureJsReport
  onContinue: (report: PureJsReport) => void
  onOpen: (report: PureJsReport) => void
  onDelete: (report: PureJsReport) => void
}

const ReportCard: React.FC<ReportCardProps> = ({ report, onContinue, onOpen, onDelete }) => {
  const { t } = useTranslation()
  const isDraft = report.status === ReportStatus.DRAFT

  return (
    <Box
      sx={{
        backgroundColor: INTERAX_COLORS.offBlack,
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Box className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '4px' }}>
          {report.title || t('facilitator.reportManagement.untitledReport')}
        </Box>
        <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {report.createdAt
            ? new Date(report.createdAt).toLocaleDateString()
            : t('facilitator.reportManagement.noDate')}
        </Box>
        <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey, marginTop: '4px' }}>
          {t('facilitator.reportManagement.sessionCount', { count: report.sessionIds?.length || 0 })}
          {' • '}
          {t('facilitator.reportManagement.teamCount', { count: report.teamIds?.length || 0 })}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: '8px' }}>
        {isDraft ? (
          <Button variant="secondary" onClick={() => onContinue(report)}>
            {t('facilitator.reportManagement.continue')}
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => onOpen(report)}>
            {t('facilitator.reportManagement.open')}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={() => onDelete(report)}
          className="!bg-red-600 hover:!bg-red-700 focus:ring-red-500/20 border-red-600"
        >
          {t('facilitator.reportManagement.delete')}
        </Button>
      </Box>
    </Box>
  )
}
